import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { API_CONFIG } from '../../config/api.config';
import {
  Search,
  Plus,
  LayoutGrid,
  Settings,
  Bell,
  Menu,
  ArrowLeft,
  Send,
  LogIn,
  LogOut,
  X,
  Heart,
  Bookmark,
  Eye
} from 'lucide-react';
import Avatar from '../common/Avatar';

export default function DocumentDetail() {
  const navigate = useNavigate();
  const location = useLocation();

  // 从URL路径中提取documentId（格式：/doc/:id，ID可以是任何字符串，包括UUID）
  const documentId = location.pathname.match(/^\/doc\/([^/]+)$/)?.[1];
  const [document, setDocument] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 点赞和收藏相关状态
  const [isLiked, setIsLiked] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [liking, setLiking] = useState(false);
  const [favoriting, setFavoriting] = useState(false);

  // 获取当前登录用户
  const getCurrentUser = () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  };
  const currentUser = getCurrentUser();

  // 设置弹窗相关状态
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  // 从 localStorage 读取翻译设置，默认为 true
  const [showTranslation, setShowTranslation] = useState(() => {
    const saved = localStorage.getItem('showTranslation');
    return saved !== null ? saved === 'true' : true;
  });

  // 切换翻译设置
  const handleToggleTranslation = () => {
    const newValue = !showTranslation;
    setShowTranslation(newValue);
    localStorage.setItem('showTranslation', newValue.toString());
    // 重新加载数据
    fetchDocumentDetail();
  };

  // 评论相关状态
  const [commentContent, setCommentContent] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);

  // 用于追踪documentId变化，避免重复调用handleClick
  const prevDocumentIdRef = useRef(null);

  // 处理登出
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  // 标记文档为已读
  const markDocumentAsRead = async () => {
    if (currentUser && document && document.rid) {
      try {
        await apiClient.post(API_CONFIG.ENDPOINTS.DOCUMENT_MARK_READ, {
          rid: document.rid,
        });
      } catch (err) {
        console.error('标记已读失败:', err);
      }
    }
  };

  // 获取文档详情
  const fetchDocumentDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      // 添加翻译参数
      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.DOCUMENT_DETAIL}?id=${documentId}&translate=${showTranslation.toString()}`);
      if (response.code === 0) {
        setDocument(response.data);
        // 更新点赞和收藏状态
        setIsLiked(response.data.is_liked || false);
        setIsFavorited(response.data.is_favorited || false);
        setLikeCount(response.data.like_count || 0);
        setFavoriteCount(response.data.favorite_count || 0);
        // 标记为已读
        await markDocumentAsRead();
      }
    } catch (err) {
      console.error('获取文档详情失败:', err);
      setError('获取文档详情失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 获取评论列表
  const fetchComments = async () => {
    try {
      setCommentsLoading(true);
      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.COMMENT_LIST}?document_id=${documentId}`);
      if (response.code === 0) {
        setComments(response.data || []);
      } else {
        console.error('获取评论列表失败:', response.message);
      }
    } catch (err) {
      console.error('获取评论列表失败:', err);
    } finally {
      setCommentsLoading(false);
    }
  };

  // 提交评论
  const handleSubmitComment = async () => {
    // 检查是否登录
    if (!currentUser) {
      // 跳转到登录页面，保存当前路径以便返回
      navigate({ pathname: 'auth', search: `redirect=${encodeURIComponent(window.location.pathname + window.location.search)}` });
      return;
    }

    // 验证输入
    if (!commentContent.trim()) {
      setCommentError('请输入评论内容');
      return;
    }

    setSubmittingComment(true);
    setCommentError('');

    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.COMMENT_ADD, {
        document_id: documentId,
        content: commentContent.trim(),
      });

      if (response.code === 0) {
        // 清空输入框
        setCommentContent('');
        setCommentError('');
        // 重新加载评论列表
        await fetchComments();
        // 更新评论数
        if (document) {
          setDocument({
            ...document,
            comment_count: (document.comment_count || 0) + 1
          });
        }
      } else {
        setCommentError(response.message || '评论提交失败');
      }
    } catch (err) {
      console.error('提交评论失败:', err);
      setCommentError('提交失败，请稍后重试');
    } finally {
      setSubmittingComment(false);
    }
  };

  // 返回列表
  const handleBack = () => {
    // 返回上一个历史URL
    navigate(-1);
  };

  // 增加点击数
  const handleClick = async () => {
    try {
      await apiClient.post(API_CONFIG.ENDPOINTS.DOCUMENT_CLICK, {
        document_id: documentId,
      });
    } catch (err) {
      console.error('增加点击数失败:', err);
    }
  };

  // 点赞/取消点赞
  const handleLike = async () => {
    // 检查是否登录
    if (!currentUser) {
      // 跳转到登录页面，保存当前路径以便返回
      navigate({ pathname: 'auth', search: `redirect=${encodeURIComponent(window.location.pathname + window.location.search)}` });
      return;
    }

    setLiking(true);
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.DOCUMENT_LIKE, {
        document_id: documentId,
      });

      if (response.code === 0) {
        setIsLiked(response.data.is_liked);
        setLikeCount(response.data.like_count);
      }
    } catch (err) {
      console.error('点赞操作失败:', err);
    } finally {
      setLiking(false);
    }
  };

  // 收藏/取消收藏
  const handleFavorite = async () => {
    // 检查是否登录
    if (!currentUser) {
      // 跳转到登录页面，保存当前路径以便返回
      navigate({ pathname: 'auth', search: `redirect=${encodeURIComponent(window.location.pathname + window.location.search)}` });
      return;
    }

    setFavoriting(true);
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.DOCUMENT_FAVORITE, {
        document_id: documentId,
      });

      if (response.code === 0) {
        setIsFavorited(response.data.is_favorited);
        setFavoriteCount(response.data.favorite_count);
      }
    } catch (err) {
      console.error('收藏操作失败:', err);
    } finally {
      setFavoriting(false);
    }
  };

  useEffect(() => {
    if (documentId) {
      fetchDocumentDetail();
      fetchComments();
      // 只在documentId变化时增加点击数
      if (prevDocumentIdRef.current !== documentId) {
        handleClick();
        prevDocumentIdRef.current = documentId;
      }
    }
  }, [documentId, showTranslation]);

  return (
    <div className="flex h-screen bg-[#f8f9fa] text-[#333] font-sans overflow-hidden">
      {/* 左侧边栏 */}
      <aside className="fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 hidden lg:flex">
        <div className="flex flex-col h-full">
          {/* Logo 区 */}
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <div className="w-8 h-8 bg-green-600 rounded flex items-center justify-center">
              <span className="text-white font-bold italic">f</span>
            </div>
            <h1 className="text-xl font-bold tracking-tight">漏斗Feed</h1>
          </div>

          {/* 频道列表 */}
          <nav className="flex-1 overflow-y-auto p-3">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-3">
              Feeds
            </div>
            {/* 全部文章 */}
            <button
              onClick={handleBack}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors mb-2 text-gray-600 hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="truncate">返回列表</span>
            </button>
          </nav>

          {/* 底部设置 */}
          <div className="p-4 border-t border-gray-100 space-y-2">
            <button
              onClick={() => setShowSettingsModal(true)}
              className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 w-full hover:bg-gray-100 rounded-md"
            >
              <Settings className="w-4 h-4" />
              设置
            </button>
          </div>
        </div>
      </aside>

      {/* 主界面内容 */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden lg:ml-64">
        {/* 顶部导航 */}
        <header className="h-16 bg-[#1a1c1e] text-white flex items-center justify-between px-4 lg:px-8 shrink-0">
          <div className="flex items-center gap-4 flex-1">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 p-1 hover:bg-white/10 rounded transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
              <span className="hidden sm:inline">返回</span>
            </button>
            <div className="relative max-w-md w-full hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search feeds..."
                className="w-full bg-white/10 border-none rounded-md py-1.5 pl-10 pr-4 text-sm focus:ring-2 focus:ring-green-500 outline-none placeholder:text-gray-500"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            {currentUser ? (
              // 已登录用户 - 显示铃铛和头像
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowNotificationPopup(!showNotificationPopup)}
                  className="p-2 hover:bg-white/10 rounded-full relative group"
                >
                  <Bell className="w-5 h-5" />
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 hover:bg-white/10 p-2 rounded-[6px] transition-colors"
                  >
                    <Avatar src={currentUser.avatar} name={currentUser.nickname || currentUser.username} />
                  </button>
                  {/* 用户下拉菜单 */}
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900 text-center">
                          {currentUser.nickname || currentUser.username}
                        </p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-2 text-center text-sm text-red-600 hover:bg-gray-50 flex items-center justify-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        退出登录
                      </button>
                    </div>
                  )}
                  {/* 通知弹窗 */}
                  {showNotificationPopup && (
                    <div className="absolute right-16 top-full mt-2 w-72 bg-white rounded-lg shadow-lg py-6 z-50">
                      <div className="flex flex-col items-center justify-center gap-3 text-gray-500">
                        <Bell className="w-12 h-12 text-gray-300" />
                        <p className="text-sm">暂无通知</p>
                      </div>
                    </div>
                  )}
                  {/* 点击外部关闭菜单 */}
                  {showUserMenu && (
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserMenu(false)}
                    />
                  )}
                  {/* 点击外部关闭通知弹窗 */}
                  {showNotificationPopup && (
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowNotificationPopup(false)}
                    />
                  )}
                </div>
              </div>
            ) : (
              // 未登录用户 - 只显示登录按钮，不显示铃铛
              <button
                onClick={() => navigate({ pathname: 'auth' })}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors"
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline">登录</span>
              </button>
            )}
          </div>
        </header>

        {/* 详情内容区域 */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <div className="max-w-4xl mx-auto">
            {/* 加载状态 */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-500">加载中...</div>
              </div>
            )}

            {/* 错误状态 */}
            {error && !loading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-red-500">{error}</div>
              </div>
            )}

            {/* 文档详情 */}
            {!loading && !error && document && (
              <div>
                {/* 文章卡片 */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h1 className="text-2xl font-bold text-gray-900 mb-4">
                    {document.title}
                  </h1>

                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                    <span className="font-medium text-gray-700">{document.author}</span>
                    <span>•</span>
                    <span>{document.time}</span>
                  </div>

                  <div
                    className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: document.summary }}
                  />

                  <a
                    href={document.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-4 text-green-600 hover:text-green-700 font-medium text-sm"
                  >
                    阅读原文
                  </a>

                  {/* 统计和操作按钮 */}
                  <div className="flex items-center gap-8 mt-6 pt-4 border-t border-gray-100">
                    {/* 点击数 */}
                    <div className="flex items-center gap-1.5 text-sm text-gray-500">
                      <Eye className="w-4 h-4" />
                      <span>{document.click_count || 0}</span>
                    </div>

                    {/* 点赞按钮 */}
                    <button
                      onClick={handleLike}
                      disabled={liking}
                      className={`flex items-center gap-1.5 text-sm transition-colors ${
                        isLiked
                          ? 'text-red-500'
                          : 'text-gray-500 hover:text-red-400'
                      } ${liking ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Heart
                        className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`}
                      />
                      <span>{likeCount || 0}</span>
                    </button>

                    {/* 收藏按钮 */}
                    <button
                      onClick={handleFavorite}
                      disabled={favoriting}
                      className={`flex items-center gap-1.5 text-sm transition-colors ${
                        isFavorited
                          ? 'text-green-600'
                          : 'text-gray-500 hover:text-green-500'
                      } ${favoriting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Bookmark
                        className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`}
                      />
                      <span>{favoriteCount || 0}</span>
                    </button>
                  </div>
                </div>

                {/* 评论区域 */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 mt-3">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-900">评论</h2>
                    <span className="text-sm text-gray-500">
                      {document.comment_count || 0} 条评论
                    </span>
                  </div>

                  {/* 添加评论表单 */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    {currentUser ? (
                      // 已登录用户
                      <div>
                        <div className="flex items-center gap-3 mb-3">
                          <Avatar src={currentUser.avatar} name={currentUser.nickname || currentUser.username} />
                          <div>
                            <div className="font-medium text-gray-900">
                              {currentUser.nickname || currentUser.username}
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <textarea
                            placeholder="写下你的评论..."
                            value={commentContent}
                            onChange={(e) => {
                              setCommentContent(e.target.value);
                              setCommentError('');
                            }}
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base resize-none"
                            style={{ fontSize: '16px' }}
                          />
                        </div>
                      </div>
                    ) : (
                      // 未登录用户
                      <div className="text-center py-4">
                        <LogIn className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-3">请登录后发表评论</p>
                        <button
                          onClick={() => navigate({ pathname: 'auth', search: `redirect=${encodeURIComponent(window.location.pathname + window.location.search)}` })}
                          className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                        >
                          <LogIn className="w-4 h-4" />
                          立即登录
                        </button>
                      </div>
                    )}
                    {/* 错误提示 */}
                    {commentError && currentUser && (
                      <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600">{commentError}</p>
                      </div>
                    )}
                    {currentUser && (
                      <button
                        onClick={handleSubmitComment}
                        disabled={submittingComment || !commentContent.trim()}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className="w-4 h-4" />
                        {submittingComment ? '提交中...' : '发表评论'}
                      </button>
                    )}
                  </div>

                  {/* 评论列表 */}
                  {commentsLoading ? (
                    <div className="text-center py-4 text-sm text-gray-500">
                      加载评论中...
                    </div>
                  ) : comments.length === 0 ? (
                    <div className="text-center py-8 text-sm text-gray-500">
                      暂无评论，快来发表第一条评论吧！
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {comments.map((comment) => (
                        <div
                          key={comment.id}
                          className="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                        >
                          <div className="flex items-center gap-3 mb-2">
                            {comment.user ? (
                              // 登录用户（未设置头像或加载失败时由 Avatar 统一兜底）
                              <Avatar src={comment.user.avatar} name={comment.user.nickname || comment.user.username} />
                            ) : (
                              // 匿名用户
                              <div className="w-8 h-8 rounded-[6px] bg-gray-100 flex items-center justify-center text-gray-600 font-semibold text-sm">
                                {comment.author.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-gray-900 text-sm">
                                {comment.user && comment.user.nickname ? comment.user.nickname : comment.author}
                              </div>
                              <div className="text-xs text-gray-400">
                                {comment.created_at}
                              </div>
                            </div>
                          </div>
                          <p className="text-gray-700 text-sm pl-11">
                            {comment.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* 设置弹窗 */}
      {showSettingsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* 背景遮罩 */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowSettingsModal(false)}
          />
          {/* 弹窗内容 */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            {/* 关闭按钮 */}
            <button
              onClick={() => setShowSettingsModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            {/* 标题 */}
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              设置
            </h3>

            {/* 设置选项 */}
            <div className="space-y-4">
              {/* 显示翻译开关 */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">显示翻译</p>
                  <p className="text-xs text-gray-500 mt-1">英文内容显示中文翻译标题</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showTranslation}
                    onChange={handleToggleTranslation}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
            </div>

            {/* 按钮 */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
